import Axios from 'axios';
import PerfectScrollbar from 'perfect-scrollbar';
import Sticky from '../../../common/components/sticky/sticky';
import { history, parseQuery, toQuery } from '../../../utils/router';
import '../../../images-svg-sprite/zip_ic.svg';
import { onScroll } from '../../../utils/on-scroll';
import { templateToNodeList, appendNodesTo } from '../../../utils/templateToNodeList';
import { isNotDesktop } from '../../../utils/isNotDesktop';
import { scrollTo } from '../../../utils/scroll';
import {
    MOBILE_HEADER_HEIGHT,
    DESKTOP_HEADER_HEIGHT,
    TAB_HEADER_HEIGHT,
    STICKED_PRODUCT_DESCRIPTION_HEIGHT,
} from '../../../common/constants';
import {
    stickProductDescriptionBlock,
    unstickProductDescriptionBlock,
} from '../product-description/product-description';
import { onSwitchFromDesktop, onSwitchToDesktop } from '../../../utils/resizeObserver';

class AbstractSwitcher {
    constructor(tabs) {
        this.tabs = Array.from(tabs.children);
        this.prevID = null;
    }

    switch() {
        throw new Error('You forgot to override method switch', this);
    }
}

class AnchorSwitcher extends AbstractSwitcher {
    constructor(tabs, onClick, getFixedHeaderOffset) {
        super(tabs);
        this.onClick = onClick;
        this.noEventHandling = false;
        this.noEventHandlingRestoreTimeout = null;
        this.getFixedHeaderOffset = getFixedHeaderOffset;
        onScroll(this.handleScroll);
        this.wrapTabs = document.querySelector('.pd-tabs');
    }

    static isElementInViewport(el) {
        const rect = el.getBoundingClientRect();
        const threshold = isNotDesktop()
            ? MOBILE_HEADER_HEIGHT + TAB_HEADER_HEIGHT
            : DESKTOP_HEADER_HEIGHT + TAB_HEADER_HEIGHT;
        const isTopVisible = rect.top >= threshold;
        return isTopVisible || rect.bottom > threshold;
    }

    isInTab = () => {
        const isSticked = document
            .querySelector('.pd-tabs__header__wrapper')
            .classList.contains('sticky');
        return isSticked || this.wrapTabs.offsetTop - window.pageYOffset <= -50;
    };

    handleScroll = () => {
        if (this.noEventHandling) return;
        try {
            const search = parseQuery(history.location.search);
            const inTabs = this.isInTab();
            const tab = this.tabs.find(AnchorSwitcher.isElementInViewport);
            const isIdEqual =
                tab.id === this.prevID || tab.classList.contains('pd-tabs__preloader');
            if ((!isIdEqual && inTabs) || (isIdEqual && inTabs && !search.get('tab_selected'))) {
                this.prevID = tab.id;
                this.noJump = true;
                this.onClick(tab.id);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line
                console.warn(`Tab content is missing`);
            }
        }
    };

    switch(id) {
        const tab = this.tabs.find(child => child.id === id);
        if (tab) {
            this.scroll(id, tab);
        }
    }

    /**
     * Get top position relative to whole page
     * @param {HTMLElement} element
     */
    getTopPosition(element) {
        return (
            window.pageYOffset + element.getBoundingClientRect().top - this.getFixedHeaderOffset()
        );
    }

    scroll = (id, element, scrollDuration = 500) => {
        this.prevID = id;
        if (this.noJump) {
            this.noJump = false;
        } else {
            const timeOut = scrollDuration + 100;
            clearTimeout(this.noEventHandlingRestoreTimeout);
            this.noEventHandling = true;
            this.noEventHandlingRestoreTimeout = setTimeout(() => {
                this.noEventHandling = false;
            }, timeOut);
            scrollTo('top', this.getTopPosition(element), { duration: scrollDuration });
        }
    };
}

function notifyTopStickyAreaChange(lastStickyElement, timeoutMs) {
    function sendNotification() {
        const elBRect = lastStickyElement.getBoundingClientRect();
        const stickyAreaHeight = elBRect.top + elBRect.height;

        const PDP_HEADER_CHANGE = new CustomEvent('PDP_HEADER_CHANGE', {
            detail: {
                stickyAreaHeight,
            },
        });

        document.body.dispatchEvent(PDP_HEADER_CHANGE);
    }

    if (typeof timeoutMs === 'undefined') {
        sendNotification();
    } else {
        // timeout to wait for sticky area animation
        setTimeout(sendNotification, timeoutMs);
    }
}

export class Tab {
    constructor({ tabsList, tabs, Switcher = AnchorSwitcher }) {
        this.tabsList = document.querySelector(tabsList);
        if (!this.tabsList) {
            throw new Error('Please provide a valid tabs links');
        }
        this.tabs = document.querySelector(tabs);
        this.initialTab = parseQuery(window.location.search).get('tab_selected');
        this.isGoingToInitial = false;
        if (!this.tabs) {
            throw new Error('Please provide a valid tabs content');
        }

        const tabSticked = new Sticky(this.tabsList.parentNode, {
            stickTo: 'top',
            stickyCondition: config => {
                if (isNotDesktop()) {
                    if (config.isSticked) {
                        return config.top < MOBILE_HEADER_HEIGHT + 1;
                    }
                    return config.top < MOBILE_HEADER_HEIGHT;
                }
                return config.top < DESKTOP_HEADER_HEIGHT;
            },

            onStickStart: () => {
                if (isNotDesktop()) {
                    notifyTopStickyAreaChange(this.tabsList.parentNode, 50);
                    return;
                }

                stickProductDescriptionBlock(
                    this.tabsList.parentNode.getBoundingClientRect().height
                );
                notifyTopStickyAreaChange(this.tabsList.parentNode, 250);
            },
            onStickRelease() {
                unstickProductDescriptionBlock();
                const headerEl = document.querySelector('.js-header-apc');
                if (headerEl) {
                    notifyTopStickyAreaChange(headerEl);
                }
            },
        });
        onSwitchFromDesktop(unstickProductDescriptionBlock);

        tabSticked.init();
        this.switcher = new Switcher(this.tabs, this.updateHistory, () => {
            if (isNotDesktop()) {
                return MOBILE_HEADER_HEIGHT + TAB_HEADER_HEIGHT;
            }
            return tabSticked.isSticked()
                ? STICKED_PRODUCT_DESCRIPTION_HEIGHT + TAB_HEADER_HEIGHT
                : DESKTOP_HEADER_HEIGHT + TAB_HEADER_HEIGHT + 10;
        });
        this.selectTab(this.findActive(), false);
        this.tabsList.addEventListener('click', this.handleClick);

        history.listen(location => this.selectTab(parseQuery(location.search).get('tab_selected')));
    }

    goToInitial() {
        if (this.initialTab) {
            const hasTab = !!document.querySelector(`#${this.initialTab}`);
            history.push({
                search: hasTab ? `tab_selected=${this.initialTab}` : '',
            });
            this.selectTab(hasTab ? this.initialTab : this.findActive(), hasTab);
            this.isGoingToInitial = true;
        }
    }

    initializeHorizontalScrollOnDesktop(element) {
        let ps = new PerfectScrollbar(element);
        this.addScrollbarGradient(ps.reach.x, ps.element.parentNode);
        const addGradientCallBack = () => {
            this.addScrollbarGradient(ps.reach.x, ps.element.parentNode);
        };
        element.addEventListener('ps-scroll-x', addGradientCallBack);
        return () => {
            ps.destroy();
            ps = null;
            element.removeEventListener('ps-scroll-x', addGradientCallBack);
            this.removeScrollBarGradient(element.parentElement);
        };
    }

    initializeHorizontalScroll() {
        const psSelector = '#js-tabs-scrollbar';
        const element = document.querySelector(psSelector);
        let unsubscribe = () => {};
        onSwitchFromDesktop(() => {
            unsubscribe();
            unsubscribe = this.addScrollbarGradientOnMobile(element);
        });
        onSwitchToDesktop(() => {
            unsubscribe();
            unsubscribe = this.initializeHorizontalScrollOnDesktop(element);
        });
        if (!isNotDesktop()) {
            unsubscribe = this.initializeHorizontalScrollOnDesktop(element);
        } else {
            unsubscribe = this.addScrollbarGradientOnMobile(element);
        }
    }

    static init(params) {
        return new Tab(params);
    }

    async loadContent(shouldLoad = () => true) {
        const elements = Array.from(document.querySelectorAll('.pd-tabs__tab_async'));
        const loadedContent = await Promise.all(
            elements.filter(shouldLoad).map(async element => {
                try {
                    const url = element.dataset.requestUrl;
                    const { data } = await Axios.get(url);
                    return {
                        data,
                        element,
                    };
                } catch (error) {
                    return {
                        data: '',
                        element,
                    };
                }
            })
        );

        const availableTabs = loadedContent
            .map(({ data, element }) => ({
                content: templateToNodeList(data),
                element,
            }))
            .filter(({ content, element }) => {
                if (content.length === 0) {
                    this.removeTab(element);
                    return false;
                }
                return true;
            });
        availableTabs.forEach(this.appendTabContent);

        return availableTabs.reduce((set, { element }) => {
            set.add(element.id);
            return set;
        }, new Set());
    }

    appendTabContent = ({ content, element }) => {
        appendNodesTo(content, element);
    };

    showTabs({ onEnd }) {
        const skeleton = document.querySelector('.pd-tabs__skeleton');
        skeleton.parentElement.removeChild(skeleton);
        Array.from(document.querySelectorAll('.pd-tabs__item_hidden')).forEach(element => {
            element.classList.remove('pd-tabs__item_hidden');
        });
        const tabPreloader = document.querySelector('.pd-tabs__preloader');
        tabPreloader.parentElement.removeChild(tabPreloader);
        document
            .querySelector('.pd-tabs__content_loading')
            .classList.remove('pd-tabs__content_loading');
        const asyncTabs = Array.from(document.querySelectorAll('.pd-tabs__tab_async'));
        asyncTabs.forEach(el => el.classList.remove('pd-tabs__tab_async'));
        if (onEnd) {
            onEnd(asyncTabs);
        }
    }

    removeTab(element) {
        const tabElement = document.querySelector(`button[data-tab=${element.id}]`).parentElement;
        element.parentElement.removeChild(element);
        tabElement.parentElement.removeChild(tabElement);
    }

    handleClick = event => {
        event.preventDefault();
        const { target } = event;
        if (!target.classList.contains('pd-tabs__button')) {
            return;
        }
        this.updateHistory(Tab.getTabId(target));
        this.alignTabHeader(target);
    };

    alignTabHeader = activeElement => {
        const screenWidth = window.innerWidth;
        const activeElementWidth = activeElement.getBoundingClientRect().width;
        const offset = activeElement.offsetLeft - (screenWidth - activeElementWidth) / 2;
        const tabsScrollbar = document.querySelector('#js-tabs-scrollbar');
        scrollTo({ element: tabsScrollbar, direction: 'left' }, offset, { duration: 400 });
    };

    updateHistory = id => {
        const search = parseQuery(history.location.search);
        history.push({
            search: toQuery(search.set('tab_selected', id)),
        });
    };

    findActive() {
        const search = parseQuery(history.location.search).get('tab_selected');
        return search || Tab.getTabId(this.tabsList.children[0].children[0]);
    }

    static getTabId(element) {
        return element.dataset.tab;
    }

    selectTab = (id, switchToTab = true) => {
        Array.from(this.tabsList.children).forEach(liElement => {
            const button = liElement.children[0];
            const tabId = Tab.getTabId(button);
            if (tabId && tabId === id) {
                liElement.classList.add('pd-tabs__item_active');
                this.alignTabHeader(liElement);
            } else if (id !== undefined) {
                liElement.classList.remove('pd-tabs__item_active');
            }
        });
        if (switchToTab) {
            this.switcher.switch(id);
        }
    };

    addScrollbarGradientOnMobile = element => {
        const elements = Array.from(element.children);
        let lastElementWidth = 0;
        const totalWidth = elements.reduce((widthAccumulator, child) => {
            lastElementWidth = child.getBoundingClientRect().width;
            return widthAccumulator + lastElementWidth;
        }, 0);
        const scrollHandler = () => {
            const isInStartPosition = this.tabsList.scrollLeft <= 0;
            const isInEndPosition =
                totalWidth - this.tabsList.scrollLeft - lastElementWidth <
                (isNotDesktop() ? 40 : 5);
            let position = '';
            if (isInStartPosition) {
                position = 'start';
            }
            if (isInEndPosition) {
                position = 'end';
            }
            this.addScrollbarGradient(position, this.tabsList.parentElement);
        };
        element.addEventListener('scroll', scrollHandler);
        scrollHandler();
        return () => {
            element.removeEventListener('scroll', scrollHandler);
        };
    };

    addScrollbarGradient = (position, target) => {
        if (position === 'start') {
            target.classList.add('pd-tabs__list-gradient_right');
            target.classList.remove('pd-tabs__list-gradient');
            return;
        }
        if (position === 'end') {
            target.classList.add('pd-tabs__list-gradient_left');
            target.classList.remove('pd-tabs__list-gradient');
            return;
        }
        if (!target.classList.contains('pd-tabs__list-gradient')) {
            this.removeScrollBarGradient(target);
        }
    };

    removeScrollBarGradient(element) {
        element.classList.add('pd-tabs__list-gradient');
        element.classList.remove('pd-tabs__list-gradient_left');
        element.classList.remove('pd-tabs__list-gradient_right');
    }
}
